/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The flat view of a right-nested conditional** (`X41`, `#1202`).
 *
 * `ConditionalExpression` nests: an `else` may be another conditional, and the engine has always
 * resolved that as first-match-wins. `#1202` measured it rather than proposing a DTO change —
 * *"a case ladder is a right-nested `else` chain and the engine has always resolved it"* — so this
 * is a **projection**, exactly as {@link flattenPredicate} is for `CriteriaNode`, and for the same
 * reason: it is the part with a rule in it, and a component test that clicks `+ Add Case` passes
 * against a projection that loses half the model.
 *
 * ## Why a flat view at all
 *
 * Expressed as nested if/else, the second condition hides **inside the first one's else branch**, so
 * an operator reading the expression cannot see the order that decides it. `X41`: *the order is the
 * thing that needs reviewing, so it is the thing on the page.*
 *
 * ## It always succeeds, and `E4` is why
 *
 * This used to return `null` for a rung whose outcome is itself a condition — the other nesting axis
 * — and the editor blanked the **whole ladder** and said so. That was honest while the recursion was
 * unbounded and it is the wrong level to say it at (`#1248`: *state a claim once, at the level it is
 * true at*): one rung the view could not follow suppressed four it could, so a three-case ladder went
 * unreadable because its third case went one level deep.
 *
 * `#1272` gives the recursion a bound, so *how deep* replaces *whether*, and depth is a property of
 * the **rendering**. The flattening now always succeeds and `BranchExpressionForm` says so at the one
 * branch that is too deep, leaving the rest of the ladder editable. The refusal did not disappear; it
 * moved to the level it is true at.
 *
 * ## The round trip is the correctness proof
 *
 * `ladderFromRungs(flattenCaseLadder(x))` must equal `x` — `N11e`'s rule, asserted in the spec.
 * That is what makes a rung's own `description` part of the model rather than a detail: a nested
 * conditional in the chain may carry a note, and a projection that dropped it would lose an
 * operator's sentence on the first reorder.
 */

import type { ConditionalExpression, CriteriaNode, MacroExpression } from '../../../types/decorator-expression';

/**
 * **How many conditionals may nest before the editor stops descending** (`E4`, `#1272`).
 *
 * Counted on the **`then`** axis, which is the recursion — a ladder's `else` chain is not nesting to
 * a reader, it is a list, and `flattenCaseLadder` is what makes that true on screen. Level 1 is the
 * value's own conditional; a rung answering with a conditional is level 2; the branches of *that* are
 * where the editor stops.
 *
 * **It bounds the reader, not the document.** `libs/dto` has always allowed `then: MacroExpression`,
 * the engine resolves any depth, and a document authored elsewhere may be deeper — so what sits above
 * this bound is a **rendering** refusal that says so and touches nothing.
 *
 * **Not `MAX_EXPRESSION_REFERENCE_DEPTH`**, which is 8 and is a different kind of number: that one
 * exists because `a → b → a` does not terminate, and its own docblock says it *"is not a judgement
 * about how much nesting is reasonable"*. This one is exactly that judgement.
 *
 * **The same bound in both containers** — the row's expansion and the library page — because what
 * runs out at three levels is the reader rather than the space, and a bound that differed by
 * container would make the same value legal in one place and not the other.
 */
export const MAX_CONDITIONAL_NESTING = 2;

/**
 * One rung: *when this holds, answer with that.*
 *
 * `description` is the note on the conditional this rung came from. Every rung after the first was
 * its own `ConditionalExpression` in the stored document, so each has a slot for one.
 */
export interface CaseRung {
  condition: CriteriaNode;
  then: MacroExpression;
  description?: string;
}

/** A conditional as the ladder shows it: ordered rungs, then one final answer. */
export interface CaseLadder {
  rungs: CaseRung[];
  /**
   * The final answer, taken when no rung matched.
   *
   * **`undefined` is a third state and must not be collapsed to a value** (`#1251`). A stored
   * conditional with no `else` resolves to `{ resolved: false }`, which on an ORTB body leaf is the
   * **only** spelling of *accept the system default* — the driver's own value stands. `X41` says
   * *`Otherwise` is always present and always answered*, and that is a statement about the **editor
   * offering** the question, not a licence to invent an answer for a document that never gave one.
   * Rendering `undefined` as an empty static would silently convert *let the driver decide* into
   * *send nothing*.
   */
  otherwise: MacroExpression | undefined;
}

/**
 * Read a conditional as a ladder.
 *
 * The walk follows `else` while it is a conditional. Every level contributes one rung; the first
 * `else` that is not a conditional is the final answer, and an absent one leaves it `undefined`.
 *
 * A rung's own `then` is carried **whole and unexamined**, whatever shape it has. That is the line
 * between the two axes: this projection owns the `else` chain and nothing else, and a rung's outcome
 * — including one that is itself a decision — belongs to whoever renders the rung.
 */
export function flattenCaseLadder(expression: ConditionalExpression): CaseLadder {
  const rungs: CaseRung[] = [];
  let node: ConditionalExpression = expression;

  for (;;) {
    rungs.push({
      condition: node.condition,
      then: node.then,
      ...(node.description ? { description: node.description } : {}),
    });

    const next = node.else;
    if (next === undefined) return { rungs, otherwise: undefined };
    if (next.type === 'conditional') {
      node = next;
      continue;
    }
    return { rungs, otherwise: next };
  }
}

/**
 * Build the stored conditional from what the ladder holds.
 *
 * **Right-nested, one conditional per rung**, which is the shape the engine already resolves. A
 * ladder with no rungs is not representable and is never produced: the editor keeps at least one,
 * the same way the condition list keeps at least one condition.
 */
export function ladderFromRungs(ladder: CaseLadder): ConditionalExpression {
  const [first, ...rest] = ladder.rungs;
  if (!first) throw new Error('A case ladder has at least one rung');

  const build = (rung: CaseRung, tail: ConditionalExpression['else']): ConditionalExpression => ({
    type: 'conditional',
    condition: rung.condition,
    then: rung.then,
    // An absent `else` is preserved as absent (`#1251`), so the round trip does not manufacture an
    // answer the stored document never gave.
    ...(tail === undefined ? {} : { else: tail }),
    ...(rung.description ? { description: rung.description } : {}),
  });

  // Built from the bottom up: the last rung's `else` is the ladder's final answer, and each rung
  // above it wraps what is below.
  let tail: ConditionalExpression['else'] = ladder.otherwise;
  for (let i = rest.length - 1; i >= 0; i--) {
    tail = build(rest[i], tail);
  }
  return build(first, tail);
}

/**
 * Move a rung, returning a new ladder.
 *
 * Reordering **is** the feature rather than the polish (`X41`): a ladder you cannot reorder is a
 * nested if/else with better spacing, and first-match-wins is only reviewable if the order can be
 * changed by the person reviewing it.
 */
export function moveRung(ladder: CaseLadder, from: number, to: number): CaseLadder {
  if (from === to || from < 0 || to < 0 || from >= ladder.rungs.length || to >= ladder.rungs.length) {
    return ladder;
  }
  const rungs = [...ladder.rungs];
  const [moved] = rungs.splice(from, 1);
  rungs.splice(to, 0, moved);
  return { ...ladder, rungs };
}
